/**
 * File: reportUtils.ts
 * Path: apps/admin/src/features/diagnostics/utils/reportUtils.ts
 * Description: The diagnostics as plain text, for "Copy report": something
 * to paste into a chat with Claude or a message when asking for help.
 * Holds staff names but no customer details and no secrets.
 */

import type { CheckStatus, DiagnosticCheck } from "@idk/config";
import type { LoggedError } from "@/shared/utils/errorLogUtils";

const MARK: Record<CheckStatus, string> = { ok: "OK  ", warn: "WARN", fail: "FAIL", info: "INFO" };

const section = (title: string, checks: readonly DiagnosticCheck[]): string[] => [
  "",
  title.toUpperCase(),
  ...checks.flatMap((c) => [`[${MARK[c.status]}] ${c.label}: ${c.detail}`, ...(c.fix ? [`       Fix: ${c.fix}`] : [])]),
];

export interface ReportInput {
  now: number;
  who: string;
  role: string;
  device: string;
  deviceChecks: readonly DiagnosticCheck[];
  /** null: not an admin, or the server check didn't run. */
  systemChecks: readonly DiagnosticCheck[] | null;
  systemError: string | null;
  errors: readonly LoggedError[];
}

export const reportText = (input: ReportInput): string =>
  [
    `IDK Admin diagnostics, ${new Date(input.now).toISOString()}`,
    `Signed in: ${input.who} (${input.role}) on ${input.device}`,
    ...section("This device", input.deviceChecks),
    ...(input.systemChecks
      ? section("Whole system", input.systemChecks)
      : input.systemError
        ? ["", "WHOLE SYSTEM", `Couldn't run: ${input.systemError}`]
        : []),
    "",
    "ERRORS ON THIS DEVICE",
    ...(input.errors.length === 0
      ? ["None recorded."]
      : input.errors.map((e) => `${new Date(e.at).toISOString()} ${e.path}: ${e.message}`)),
  ].join("\n");
